import { useEffect, useRef, useState, type ComponentType } from 'react'

import { decideScroll } from './lib/scrollTarget'

// ---------------------------------------------------------------------------
// pages-routing-convention
//
// Drop a file at `src/pages/<name>.tsx` with a **default export** and it is
// served at `/<name>`. `src/pages/index.tsx` is served at `/`. That is the
// entire registration step — nobody edits this file to add a screen.
//
//   src/pages/index.tsx       -> /
//   src/pages/trips.tsx       -> /trips
//   src/pages/trip-setup.tsx  -> /trip-setup
//   src/pages/intake.tsx      -> /intake
//   src/pages/join.tsx        -> /join
//   src/pages/login.tsx       -> /login
//   src/pages/windows.tsx     -> /windows
//   src/pages/regions.tsx     -> /regions
//
// Query strings and hashes are NOT part of the route key — `/join?code=ABC`
// mounts `pages/join.tsx`, which reads `window.location.search` itself.
//
// No routing library. Do not install react-router; the glob below is the router.
// ---------------------------------------------------------------------------

const modules = import.meta.glob('./pages/*.tsx', { eager: true }) as Record<
  string,
  { default?: ComponentType }
>

const routes: Record<string, ComponentType> = {}
for (const [file, mod] of Object.entries(modules)) {
  // A page module with no default export is not a route (helpers/tests colocated
  // under pages/ are ignored rather than crashing the app).
  if (!mod.default) continue
  const name = file.replace('./pages/', '').replace(/\.tsx$/, '')
  routes[name === 'index' ? '/' : `/${name}`] = mod.default
}

/** `/trips/` and `/trips` are the same route; `''` is `/`. */
function normalizePath(pathname: string): string {
  const trimmed = pathname.replace(/\/+$/, '')
  return trimmed === '' ? '/' : trimmed
}

/** Every mounted route path, sorted. Exported for the home page's route index. */
export function listRoutes(): string[] {
  return Object.keys(routes).sort()
}

/**
 * Set by {@link navigate} and consumed by the router on the very next popstate.
 *
 * The router dispatches a synthetic PopStateEvent, so the handler cannot
 * otherwise tell "we pushed this" from "the user hit back" — and the two want
 * opposite scroll behaviour (#58). `dispatchEvent` is synchronous, so this is
 * read in the same tick it is written and never outlives the call.
 */
let pushedByRouter = false

/**
 * Client-side navigation. Push the URL and tell the router to re-read it.
 * `<a href="/trips">` also works — same-origin anchor clicks are intercepted.
 */
export function navigate(to: string): void {
  const current = location.pathname + location.search + location.hash
  if (to === current) return
  history.pushState(null, '', to)
  pushedByRouter = true
  dispatchEvent(new PopStateEvent('popstate'))
}

function NotFound({ path }: { path: string }) {
  return (
    <main className="shell">
      <h1>Not found</h1>
      <p className="sub">
        No page is mounted at <code>{path}</code>.
      </p>
      <p>
        A screen at <code>{path}</code> exists once someone adds{' '}
        <code>src/pages{path}.tsx</code> with a default export. Mounted routes:
      </p>
      <ul>
        {listRoutes().map((route) => (
          <li key={route}>
            <a href={route}>{route}</a>
          </li>
        ))}
      </ul>
    </main>
  )
}

export default function App() {
  const [path, setPath] = useState(() => normalizePath(location.pathname))
  /** Whether the pending render arrived via navigate() rather than back/forward. */
  const pushed = useRef(false)

  useEffect(() => {
    const onPop = () => {
      pushed.current = pushedByRouter
      pushedByRouter = false
      setPath(normalizePath(location.pathname))
    }
    addEventListener('popstate', onPop)
    return () => removeEventListener('popstate', onPop)
  }, [])

  // Put the viewport where a real navigation would have put it (#58). Runs after
  // the new page has rendered, so a `/page#section` link can find its element.
  // Skips the first mount: the browser has already positioned that one, and
  // stealing the scroll on a deep link would undo it.
  const firstRender = useRef(true)
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false
      return
    }
    const target = decideScroll({
      hash: location.hash,
      pushed: pushed.current,
      hasElement: (id) => document.getElementById(id) !== null,
    })
    pushed.current = false

    if (target.kind === 'top') window.scrollTo(0, 0)
    else if (target.kind === 'element') document.getElementById(target.id)?.scrollIntoView()
  }, [path])

  // Plain same-origin anchors navigate client-side, so pages can link with <a>
  // and never learn about the router. Modified clicks, new tabs, downloads and
  // external links keep their default behaviour.
  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      if (event.defaultPrevented || event.button !== 0) return
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
      const target = event.target
      const anchor = target instanceof Element ? target.closest('a') : null
      if (!anchor) return
      const href = anchor.getAttribute('href')
      if (!href || href.startsWith('#')) return
      if (anchor.target && anchor.target !== '_self') return
      if (anchor.hasAttribute('download')) return
      if (anchor.getAttribute('rel')?.includes('external')) return
      const url = new URL(href, location.href)
      if (url.origin !== location.origin) return
      event.preventDefault()
      navigate(url.pathname + url.search + url.hash)
    }
    document.addEventListener('click', onClick)
    return () => document.removeEventListener('click', onClick)
  }, [])

  const Page = routes[path]
  if (!Page) return <NotFound path={path} />
  return <Page />
}

export { App }
